"use client"

import { useEffect, useState } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation, Keyboard, Thumbs } from "swiper/modules"
import { ChevronLeft, ChevronRight, X, Maximize2, Share2 } from "lucide-react"
import Image from "next/image"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { Swiper as SwiperType } from "swiper"

// Import Swiper styles
import "swiper/css"
import "swiper/css/navigation"
import CustomButton from "../../components/CustomButton"

type ImageType = {
  url: string
  alt?: string
  description?: string
}

type ProductGalleryProps = {
  images: ImageType[]
  product: {
    title?: string
    discount_percent: number
    discount_title: string
    thumbnail?: ImageType
  }
}

export default function ProductGallery({ images, product }: ProductGalleryProps) {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null)
  const [modalSwiper, setModalSwiper] = useState<SwiperType | null>(null)

  const displayImages: ImageType[] = product?.thumbnail?.url
    ? [
      {
        url: product.thumbnail.url,
        alt: product.title || "تصویر اصلی محصول",
      },
      ...images,
    ]
    : images

  useEffect(() => {
    if (isGalleryOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }

    return () => {
      document.body.style.overflow = ""
    }
  }, [isGalleryOpen])
  console.log("displayImages", displayImages);



  if (displayImages.length === 0) return null


  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.title || "محصول",
          text: "نگاهی به این محصول بیندازید",
          url: window.location.href,
        })
      } catch (err) {
        console.log("Error sharing:", err)
      }
    }
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  return (
    <div className="w-full" dir="rtl">
      {/* Main Slider */}
      <div className="relative overflow-hidden bg-muted/30">
        <Swiper
          modules={[Navigation, Keyboard, Thumbs]}
          thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
          navigation={{
            prevEl: ".main-prev",
            nextEl: ".main-next",
          }}
          keyboard={{ enabled: true }}
          slidesPerView={1}
          spaceBetween={0}
          loop={displayImages.length > 1}
          speed={400}
          onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
          className="w-full aspect-square"
        >
          {displayImages.map((img, index) => (
            <SwiperSlide key={index}>
              <div
                className="relative w-full h-full cursor-pointer group"
                onClick={() => {
                  setCurrentIndex(index)
                  setIsGalleryOpen(true)
                }}
              >
                <Image
                  src={img.url || "/placeholder.svg"}
                  alt={img.description || img.alt || product.title || "تصویر محصول"}
                  fill
                  className="object-cover transition-transform duration-300"
                  sizes="(max-width: 768px) 100vw, 600px"
                  priority={index === 0}
                />
                {product.discount_percent > 0 && (
                  <Badge variant="destructive" className="absolute top-4 right-4 text-sm font-primary-bold shadow-lg z-10">
                    {product.discount_percent}% {product.discount_title}
                  </Badge>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Navigation Buttons */}
        {displayImages.length > 1 && (
          <>
            <CustomButton
              size="icon"
              variant="secondary"
              className="main-prev absolute right-2 top-1/2 -translate-y-1/2 z-20 opacity-100 shadow-lg"
            >
              <ChevronRight className="w-5 h-5" />
            </CustomButton>
            <CustomButton
              size="icon"
              variant="secondary"
              className="main-next absolute left-2 top-1/2 -translate-y-1/2 z-20 opacity-100 shadow-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </CustomButton>
          </>
        )}

        {/* Image Counter */}
        {displayImages.length > 1 && (
          <div className="absolute bottom-4 left-4 bg-black/70 rounded-xs text-white px-3 py-1.5 text-sm font-medium z-10">
            {currentIndex + 1} / {displayImages.length}
          </div>
        )}
      </div>

      {/* Thumbnail Slider */}
      {displayImages.length > 1 && (
        <div className="relative mt-4">
          <Swiper
            modules={[Navigation, Thumbs]}
            watchSlidesProgress
            slidesPerView={6}
            spaceBetween={12}
            freeMode
            loop={false}
            speed={400}
            navigation={{
              prevEl: ".thumb-prev",
              nextEl: ".thumb-next",
            }}
            breakpoints={{
              640: { slidesPerView: 5, spaceBetween: 12 },
              1024: { slidesPerView: 7, spaceBetween: 16 },
            }}
            onSwiper={setThumbsSwiper}
            className="w-full"
          >
            {displayImages.map((img, index) => (
              <SwiperSlide key={index}>
                <div className="relative aspect-square overflow-hidden cursor-pointer border-2 border-transparent opacity-60 hover:opacity-100 transition-all duration-300 [&.swiper-slide-thumb-active]:opacity-100 [&.swiper-slide-thumb-active]:border-primary [&.swiper-slide-thumb-active]:shadow-lg">
                  <Image
                    src={img.url}
                    alt={img.alt || "تصویر"}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 25vw, 120px"
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {displayImages.length > 5 && (
            <>
              <Button
                size="icon"
                variant="ghost"
                className="thumb-prev absolute -right-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 shadow-md"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="thumb-next absolute -left-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 shadow-md"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      )}

      {/* Enhanced Modal */}
      {isGalleryOpen && (
        <div className="fixed inset-0 z-50 bg-white/90 animate-in fade-in duration-300" >
          {/* Header Controls */}
          <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between p-4 bg-linear-to-b from-white/80 to-transparent">
            <div className="flex items-center gap-2">
              <span className="text-foreground font-medium text-lg">
                {currentIndex + 1} / {displayImages.length}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="ghost"
                onClick={toggleFullscreen}
                className="text-foreground hover:bg-muted h-10 w-10"
              >
                <Maximize2 className="w-5 h-5" />
              </Button>



              <Button
                size="icon"
                variant="ghost"
                onClick={() => {
                  setIsGalleryOpen(false)
                }}
                className="text-foreground hover:bg-muted h-10 w-10"
              >
                <X className="w-6 h-6" />
              </Button>
            </div>
          </div>

          {/* Navigation Arrows */}
          {displayImages.length > 1 && (
            <>
              <Button
                size="icon"
                variant="secondary"
                className="modal-prev absolute right-4 top-1/2 -translate-y-1/2 z-50 h-12 w-12 shadow-2xl"
              >
                <ChevronRight className="w-6 h-6" />
              </Button>
              <Button
                size="icon"
                variant="secondary"
                className="modal-next absolute left-4 top-1/2 -translate-y-1/2 z-50 h-12 w-12 shadow-2xl"
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>
            </>
          )}

          {/* Modal Swiper */}
          <Swiper
            modules={[Navigation, Keyboard]}
            navigation={{
              prevEl: ".modal-prev",
              nextEl: ".modal-next",
            }}
            keyboard={{ enabled: true }}
            initialSlide={currentIndex}
            loop={displayImages.length > 1}
            speed={400}
            onSwiper={setModalSwiper}
            onSlideChange={(swiper) => {
              setCurrentIndex(swiper.realIndex)
            }}
            className="w-full h-full"
          >
            {displayImages.map((img, i) => (
              <SwiperSlide key={i} className="flex! items-center justify-center!">
                <Image
                  src={img.url}
                  alt={img.description || img.alt || "تصویر محصول"}
                  width={1920}
                  height={1080}
                  className="max-w-full h-svh max-h-[calc(100vh-8rem)] w-auto object-contain"
                  priority={i === currentIndex}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Thumbnail Strip in Modal */}
          {displayImages.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 z-50 p-4 bg-linear-to-t from-white/80 to-transparent">
              <div className="flex gap-2 justify-center max-w-4xl mx-auto overflow-x-auto p-4 scrollbar-hide">
                {displayImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => modalSwiper?.slideTo(index)}
                    className={`relative shrink-0 w-16 h-16 overflow-hidden border-2 transition-all duration-300 ${currentIndex === index
                      ? "border-primary scale-110 shadow-lg"
                      : "border-transparent opacity-60 hover:opacity-100"
                      }`}
                  >
                    <Image
                      src={img.url || "/placeholder.svg"}
                      alt={`Thumbnail ${index + 1}`}
                      fill
                      className="object-cover"
                      sizes="64px"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
